<template>
<div class="page-container">
  <!-- Widget for the chart -->
  <div class="widget chart-widget">
    <div class="chart-header" v-if="chartLoaded && candlestickSeries && data.length">
      <div class="header-left">
        <span class="pair-info">
          {{ selectedSymbol }} | {{ formattedDateRange }} | {{ selectedInterval }}
        </span>
        <span
            class="ohlc-info"
            :style="{ color: ohlc.close > ohlc.open ? '#2ECC71' : '#E74C3C' }"
        >
          O: {{ ohlc.open }} | H: {{ ohlc.high }} | L: {{ ohlc.low }} |
          C: {{ ohlc.close }} | V: {{ ohlc.volume }}
        </span>
      </div>
      <div class="header-buttons-with-tags">
        <div class="indicator-tags">
          <span
              v-for="(indicator, index) in indicatorsWithValues"
              :key="indicator.uniqueKey"
              class="indicator-tag"
          >
            <span class="indicator-color" :style="{ backgroundColor: indicator.color }"></span>
            {{ indicator.label }}
            <span v-if="indicator.displayValue !== 'N/A'" :style="{ color: indicator.color }">
              : {{ indicator.displayValue }}
            </span>
            <span v-else>: No value</span>
            <span class="remove-icon" @click="removeIndicator(indicator.uniqueKey, index)">×</span>
          </span>
        </div>
        <div class="header-buttons">
          <button class="add-indicator-btn" @click="openPopup">Indicator</button>
          <input type="checkbox" v-model="showIndicators" class="toggle-indicator-widget" />
          <span class="reset-icon" @click="resetZoom" title="Reset Zoom">
            <span class="material-symbols-outlined">refresh</span>
          </span>
        </div>
      </div>
    </div>

    <div class="chart-container">
      <div ref="chartContainer" class="chart" style="width: 100%; height: 500px;"></div>
    </div>

    <!-- Conteneur global avec flexbox pour bien séparer les deux parties -->
    <div class="chart-header-container">


      <div class="chart-controls">
        <!-- 🔄 Spinner affiché uniquement quand `loading` est true -->
        <span v-if="loading" class="loader"></span>

        <!-- 🔤 Champ texte libre pour entrer le symbole à évaluer -->
        <input
            type="text"
            v-model="selectedSymbol"
            placeholder="Enter symbol (e.g., BTC)"
            class="compact-input symbol-input"
        />

        <!-- 📅 Champs date sans restriction -->
        <input
            type="datetime-local"
            v-model="startDate"
            class="compact-input date-input"
        />
        <input
            type="datetime-local"
            v-model="endDate"
            class="compact-input date-input"
        />

        <!-- ⏱️ Intervalle -->
        <select v-model="selectedInterval" class="compact-input select-input">
          <option v-for="interval in intervals" :key="interval" :value="interval">
            {{ interval }}
          </option>
        </select>

        <!-- 🚀 Lancer l'évaluation -->
        <button class="getButton" :disabled="!selectedSymbol" @click="evaluate">Evaluate</button>
      </div>

      <div class="position-controls mt-2">
        <label class="block font-semibold mb-1">📂 Fichiers de simulation :</label>
        <ul class="position-file-list">
          <li v-for="file in files" :key="file" class="file-entry" @click="selectFile(file)">
            📄 {{ file }}
          </li>
        </ul>
      </div>

    </div>
  </div>

  <div v-if="showIndicators" class="indicator-widget">
    <table>
      <tbody>
      <tr v-for="(row, rowIndex) in splitIndicators" :key="rowIndex">
        <td v-for="(indicator, colIndex) in row" :key="`${rowIndex}-${colIndex}`" class="indicator-box">
          {{ indicator.label }}: {{ indicator.displayValue }}
        </td>
      </tr>
      </tbody>
    </table>
  </div>

  <!-- Overlay -->
  <div v-if="isPopupVisible" class="overlay" @click="closePopup"></div>

  <!-- Add Indicator Pop-up -->
  <div v-if="isPopupVisible" class="indicator-popup">
    <div class="popup-header">Add Indicator</div>
    <div class="popup-body">
      <label for="indicator-select" class="popup-label">Select Indicator</label>
      <select id="indicator-select" v-model="selectedIndicator" class="popup-select">
        <option v-for="indicator in availableIndicators" :key="indicator.type" :value="indicator.type">
          {{ indicator.label }}
        </option>
      </select>

      <!-- Dynamic fields based on the selected indicator -->
      <div v-for="(value, key) in indicatorParams" :key="key" class="popup-field">
        <label :for="key" class="popup-label">{{ key }}</label>
        <input
            type="number"
            :id="key"
            v-model="indicatorParams[key]"
            class="popup-input"
        />
      </div>
    </div>
    <div class="popup-footer">
      <button @click="closePopup" class="popup-button popup-cancel">Cancel</button>
      <button @click="addIndicatorToChart" class="popup-button popup-add">Add</button>
    </div>
  </div>
</div>
</template>


<script>
import { createChart } from 'lightweight-charts'; // Import de la librairie
import { sma, ema, wma, rsi, macd, atr, bollingerbands } from 'technicalindicators';
import {evalApi} from "@/api.js"; // Import des indicateurs techniques

export default {
  data() {
    return {
      // Informations pour le graphique
      chartLoaded: false,
      currentPair: "",
      currentStartTime: "",
      currentEndTime: "",
      currentInterval: "",

      exchange: "BINANCE",
      pair: "BTCUSDT",
      startTime: "2024-12-01",
      endTime: "2024-12-31",
      interval: "1h",
      exchanges: ["BINANCE", "KRAKEN", "COINBASE"],
      pairs: ["BTCUSDT", "ETHUSDT", "XRPUSDT"],
      files: [],
      loading: false, // Track loading state
      sortColumn: '',
      sortOrder: 'asc',

      //graphique
      data: [],
      activeIndicators: [], // Liste des indicateurs actifs
      chartInstance: null,
      candlestickSeries: null, // Référence à la série des bougies

      rsiPriceScale: null, // Separate Y-axis for RSI

      series: {}, // Stocke les séries des indicateurs
      availableIndicators: [
        { type: "sma", label: "SMA", params: { period: 5 } },
        { type: "ema", label: "EMA", params: { period: 10 } },
        { type: "wma", label: "WMA", params: { period: 10 } },
        { type: "rsi", label: "RSI", params: { period: 14 } },
        { type: "macd", label: "MACD", params: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
        { type: "atr", label: "ATR", params: { period: 14 } },
        { type: "bollingerbands", label: "Bollinger bands", params: { period: 20, stdDev: 2 } },
      ],

      macdValues: {
        macd: 'N/A',
        signal: 'N/A',
        histogram: 'N/A',
      },

      //indicators
      isPopupVisible: false, // Controls the visibility of the pop-up
      selectedIndicator: "", // Holds the currently selected indicator
      indicatorParams: {}, // Holds the parameters for the selected indicator

      trades: [],

      ohlc: {
        open: null,
        high: null,
        low: null,
        close: null,
        volume: null,
      },

      displayedIndicators: [
        { type: "sma", label: "SMA", params: { period: 7 } },
        { type: "sma", label: "SMA", params: { period: 25 } },
        { type: "sma", label: "SMA", params: { period: 99 } },
        { type: "ema", label: "EMA", params: { period: 7 } },
        { type: "ema", label: "EMA", params: { period: 25 } },
        { type: "ema", label: "EMA", params: { period: 99 } },
        { type: "wma", label: "WMA", params: { period: 10 } },
        { type: "rsi", label: "RSI", params: { period: 6 } },
        { type: "rsi", label: "RSI", params: { period: 12 } },
        { type: "rsi", label: "RSI", params: { period: 24 } },
        { type: "atr", label: "ATR", params: { period: 14 } },
        /*{ type: "macd", label: "MACD", params: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
        { type: "bollingerbands", label: "Bollinger bands", params: { period: 20, stdDev: 2 } }*/
      ],

      japaneseCandlesticks: [
        "Doji", "Dragonfly Doji", "Gravestone Doji", "Hammer", "Inverted Hammer", "Hanging Man", "Shooting Star",
        "Bullish Engulfing", "Bearish Engulfing", "Piercing Line", "Dark Cloud Cover", "Morning Star", "Evening Star",
        "Three White Soldiers", "Three Black Crows", "Harami", "Harami Cross", "Tweezers Top", "Tweezers Bottom",
        "Marubozu", "Spinning Top", "Belt Hold", "Kicker", "Rising Three Methods", "Falling Three Methods",
        "Three Inside Up", "Three Inside Down", "Upside Gap Two Crows", "Downside Gap Two Crows",
        "Island Reversal", "Breakaway", "On Neck", "In Neck", "Thrusting", "Tasuki Gap", "Mat Hold",
        "Stick Sandwich", "High Wave", "Long-Legged Doji", "Rickshaw Man", "Matching Low", "Matching High",
        "Bullish Abandoned Baby", "Bearish Abandoned Baby", "Bullish Tri-Star", "Bearish Tri-Star",
        "Bullish Separating Lines", "Bearish Separating Lines", "Concealing Baby Swallow", "Unique Three River Bottom"
      ],

      showIndicators: false,


      selectedSymbol: '',
      symbolCounts: {},
      sortedSymbols: [],
      startDate: '',
      endDate: '',
      minEndDate: '',
      maxStartDate: '',
      selectedInterval: '15m',
      intervals: ['1m', '3m', '5m', '15m'],
      candles: [],
      chart: null,

      saveToJson: false,

      error: null,
      positions: [],
      meta: null,

      timeDifference : 1 * 60 * 60 // UTC+2 heure d'été suisse
    };
  },
  methods: {
    async fetchAvailableFiles() {
      try {
        const res = await evalApi.get('analyzer/positions-list');

        console.log("fetchAvailableFiles, res:",res);

        this.files = res.data.files || [];
      } catch (err) {
        console.error('❌ Erreur lors du chargement des fichiers :', err);
        this.error = "Erreur lors du chargement de la liste des fichiers.";
      }
    },

    async selectFile(filename) {
      this.loading = true;
      this.error = null;

      try {
        const res = await evalApi.get(`analyzer/load-from-file/${filename}`);
        this.positions = res.data.positions;
        this.candles = res.data.candles;
        this.meta = res.data.meta;

        console.log("📥 Positions :", this.positions);
        console.log("📊 Bougies :", this.candles);

        // 🔁 Conversion timestamp si besoin
        this.data = this.candles.map(c => ({
          time: c.time - this.timeDifference, // si nécessaire
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
          volume: c.volume
        }));

        // ✅ Mise à jour du graphique après chargement des données
        this.updateChart();
        this.addTradeMarkers();

      } catch (err) {
        console.error('❌ Erreur lors du chargement du fichier :', err);
        this.error = "Erreur lors du chargement du fichier " + filename;
      } finally {
        this.loading = false;
      }
    },

    addTradeMarkers() {
      if (!this.candlestickSeries || !this.positions || this.positions.length === 0) {
        console.warn("⛔️ Aucun graphique ou position disponible pour afficher les marqueurs.");
        return;
      }

      const markers = [];

      this.positions.forEach((pos) => {
        const isLong = pos.direction === 'LONG';

        const rawEntry = typeof pos.entryDate === 'string' ? Date.parse(pos.entryDate) : pos.entryDate;
        const rawExit  = typeof pos.exitDate === 'string' ? Date.parse(pos.exitDate) : pos.exitDate;

        if (!rawEntry || !rawExit || isNaN(rawEntry) || isNaN(rawExit)) {
          console.warn("⚠️ Position ignorée à cause de dates invalides :", pos);
          return;
        }

        const entryTime = Math.floor(rawEntry / 1000) - this.timeDifference;
        const exitTime  = Math.floor(rawExit / 1000)  - this.timeDifference;

        const pnlText = typeof pos.pnl === 'number' ? `${pos.pnl.toFixed(2)}$` : 'n/a';
        const roiText = typeof pos.roiNet === 'number' ? `${pos.roiNet.toFixed(2)}%` : 'n/a';

        // 🟢 Entrée : triangle vers le haut en dessous
        markers.push({
          time: entryTime,
          position: 'belowBar',
          color: isLong ? '#2ECC71' : '#E74C3C',
          shape: 'arrowUp',
          text: `${isLong ? 'LONG' : 'SHORT'} entry @ ${pos.entryPrice}`
        });

        // 🔴 Sortie : triangle vers le bas au-dessus
        markers.push({
          time: exitTime,
          position: 'aboveBar',
          color: isLong ? '#2ECC71' : '#E74C3C',
          shape: 'arrowDown',
          text: `${isLong ? 'LONG' : 'SHORT'} exit @ ${pos.exitPrice} | PnL: ${pnlText} | ROI: ${roiText}`
        });
      });

      console.log(`📍 ${markers.length} marqueurs ajoutés`, markers);

      this.candlestickSeries.setMarkers(markers);
    },

    initializeChart() {
      const chartContainer = this.$refs.chartContainer;

      this.chartInstance = createChart(chartContainer, {
        layout: { background: { color: '#181C1F' }, textColor: '#A1A5B0' },
        grid: { vertLines: { color: '#2A2A2A' }, horzLines: { color: '#2A2A2A' } },
        timeScale: { borderColor: '#3C3C3C' },
        // 🕒 Personnalisation de l'axe X
        localization: {
          timeFormatter: (timestamp) => this.formatTimestamp(timestamp)
        }
      });

      // Add candlestick series with its own price scale
      this.candlestickSeries = this.chartInstance.addCandlestickSeries({
        upColor: '#2ECC71',
        downColor: '#E74C3C',
        borderUpColor: '#2ECC71',
        borderDownColor: '#E74C3C',
        wickUpColor: '#2ECC71',
        wickDownColor: '#E74C3C',
        priceScaleId: 'main', // Use main price scale for candlesticks
      });

      this.chartLoaded = true;
      this.trackMouseOnChart();

      this.chartInstance.subscribeClick(param => {
        if (!param.time) return;

        const clickedTime = param.time;

        const matchedMarker = this.positions.find(pos => {
          const entry = Math.floor(new Date(pos.entryDate).getTime() / 1000) - this.timeDifference;
          const exit = Math.floor(new Date(pos.exitDate).getTime() / 1000) - this.timeDifference;
          return clickedTime === entry || clickedTime === exit;
        });

        if (matchedMarker) {
          const isLong = matchedMarker.direction === 'LONG';
          const price = clickedTime === Math.floor(new Date(matchedMarker.entryDate).getTime() / 1000) - this.timeDifference
              ? matchedMarker.entryPrice
              : matchedMarker.exitPrice;

          alert(`${isLong ? 'LONG' : 'SHORT'} @ ${price}`);
        }
      });
    },

    trackMouseOnChart() {
      this.chartInstance.subscribeCrosshairMove((param) => {
        if (!param || !param.time || !param.seriesData) {
          return;
        }

        // Stocker temporairement les valeurs pour MACD
        const macdTempValues = {};

        param.seriesData.forEach((data, series) => {
          const seriesTitle = series.customTitle; // Récupère le titre personnalisé de la série


          this.activeIndicators.forEach((indicator) => {
            const seriesKey = indicator.uniqueKey;

            if (indicator.type === 'macd') {

              const macdSeriesKey = `${seriesKey}-macd`;
              const signalSeriesKey = `${seriesKey}-signal`;
              const histogramSeriesKey = `${seriesKey}-histogram`;

              console.debug('MACD, current seriesTitle:', seriesTitle, ", macdSeriesKey:",macdSeriesKey, ", signalSeriesKey:", signalSeriesKey, ", histogramSeriesKey:",histogramSeriesKey);

              if (seriesTitle === macdSeriesKey) {
                macdTempValues.macd = data.value?.toFixed(2) || 'N/A';
              } else if (seriesTitle === signalSeriesKey) {
                macdTempValues.signal = data.value?.toFixed(2) || 'N/A';
              } else if (seriesTitle === histogramSeriesKey) {
                macdTempValues.histogram = data.value?.toFixed(2) || 'N/A';
              }
            } else if (seriesTitle === seriesKey) {
              // Pour les autres indicateurs
              indicator.currentValue = data.value !== undefined
                  ? data.value.toFixed(2)
                  : 'N/A';
            }
          });
        });

        // Mettre à jour les valeurs du MACD
        if (Object.keys(macdTempValues).length > 0) {
          this.macdValues = {
            macd: macdTempValues.macd || 'N/A',
            signal: macdTempValues.signal || 'N/A',
            histogram: macdTempValues.histogram || 'N/A',
          };
        }

        const pnlPoint = param.seriesData.get(this.pnlSeries);

        if (pnlPoint) {
          const pnlIndicator = this.activeIndicators.find(ind => ind.type === "pnl");
          if (pnlIndicator) {
            pnlIndicator.currentValue = pnlPoint.value.toFixed(2);
          }
        }

        // 🔍 Récupérer les valeurs de scores à la position actuelle
        const scoresAtCursor = {
          binance: "N/A",
          btcc: "N/A",
          okx: "N/A"
        };

        // Vérifier chaque série de score
        if (this.binanceScoreSeries) {
          const binanceData = param.seriesData.get(this.binanceScoreSeries);
          if (binanceData) scoresAtCursor.binance = binanceData.value.toFixed(2);
        }

        if (this.btccScoreSeries) {
          const btccData = param.seriesData.get(this.btccScoreSeries);
          if (btccData) scoresAtCursor.btcc = btccData.value.toFixed(2);
        }

        if (this.okxScoreSeries) {
          const okxData = param.seriesData.get(this.okxScoreSeries);
          if (okxData) scoresAtCursor.okx = okxData.value.toFixed(2);
        }

        // Gestion des OHLC (dernière bougie survolée)
        const candle = this.data.find((item) => item.time === param.time);
        if (candle) {
          this.ohlc = {
            open: candle.open,
            high: candle.high,
            low: candle.low,
            close: candle.close,
            volume: candle.volume,
          };

          this.updateIndicators(candle);


        } else {
          this.ohlc = { open: null, high: null, low: null, close: null, volume: null };
        }

        // Forcer la mise à jour de l'interface
        this.$forceUpdate();
      });
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "";

      const date = new Date((timestamp + 3600) * 1000); // Ajoute 1h et convertit en ms
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    // Method to format timestamp to YYYY-MM-DD HH:MM
    formatDate(timestamp) {

      if (!timestamp) return ""; // Return empty string if timestamp is invalid or undefined

      // Convert from milliseconds to seconds
      const date = new Date(Number(timestamp));

      if (isNaN(date.getTime())) return ""; // Check if the date is valid

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    resetZoom() {
      if (this.chartInstance) {
        // Reset time scale to fit all data
        this.chartInstance.timeScale().fitContent();

        // Reset price scale to fit the range of data
        this.chartInstance.priceScale("right").applyOptions({
          autoScale: true,
        });
      }
    },

    clearChart() {
      if (this.chartInstance) {
        // Supprimer toutes les séries avant de créer un nouveau graphique
        this.chartInstance.removeSeries(this.chartInstance.series);
        this.chartInstance = null; // Réinitialiser l'instance du graphique
      }
    },

    formatPair(pair) {
      return pair.replace(/([A-Z]+)([A-Z]{4})/, '$1 / $2');
    },
    formatDateRange(startDate, endDate) {
      return `${startDate} - ${endDate}`;
    },

    //indicators
    openPopup() {
      this.isPopupVisible = true;
      this.selectedIndicator = this.availableIndicators[0]?.type || "";
      this.updateIndicatorParams();
    },
    closePopup() {
      this.isPopupVisible = false;
      this.selectedIndicator = "";
      this.indicatorParams = {};
    },
    updateIndicatorParams() {
      const selected = this.availableIndicators.find(ind => ind.type === this.selectedIndicator);
      if (selected) {
        this.indicatorParams = { ...selected.params };
      }
    },
    addIndicatorToChart() {
      const selected = this.availableIndicators.find(ind => ind.type === this.selectedIndicator);
      if (!selected) return;

      const params = { ...selected.params, ...this.indicatorParams };
      const color = this.generateRandomColor();
      params.color = color; // Ajoutez la couleur pour l'utiliser dans le graphique
      const uniqueKey = this.generateUniqueKey(selected.type, params);

      if (this.activeIndicators.some(ind => ind.uniqueKey === uniqueKey)) {
        console.warn(`Indicator "${uniqueKey}" already exists.`);
        return;
      }

      this.addIndicator(selected.type, params);

      this.activeIndicators.push({
        type: selected.type,
        label: `${selected.label}`,
        params,
        uniqueKey,
        color,
        currentValue: null, // Initialize current value
      });

      // Fermer le popup après l'ajout
      this.closePopup();

      console.log("addIndicatorToChart, this.activeIndicators:",this.activeIndicators);
      console.log("addIndicatorToChart, this.indicatorsWithValues:",this.indicatorsWithValues);
    },

    removeIndicator(uniqueKey, index) {
      console.log('removeIndicator, existing series:', Object.keys(this.series));

      if (uniqueKey.includes('macd')) {
        const keysToRemove = [`${uniqueKey}-macd`, `${uniqueKey}-signal`, `${uniqueKey}-histogram`];
        keysToRemove.forEach((key) => {
          const series = this.series[key];
          if (series) {
            this.chartInstance.removeSeries(series);
            delete this.series[key];
          }
        });
      }
      // Handle Bollinger Bands (removing multiple series)
      else if (uniqueKey.includes('bollingerbands')) {
        // Define the keys for middle, upper, and lower bands
        const keysToRemove = [`${uniqueKey}`, `${uniqueKey}-upper`, `${uniqueKey}-lower`];

        // Iterate over each key to remove its corresponding series
        keysToRemove.forEach((key) => {
          const series = this.series[key];
          if (series) {
            this.chartInstance.removeSeries(series); // Remove the series from the chart
            delete this.series[key]; // Delete the series reference
            console.debug('Removed series:', key);
          } else {
            console.warn('Series not found for:', key);
          }
        });
      } else {
        // Handle single series indicators
        const series = this.series[uniqueKey];
        if (series) {
          this.chartInstance.removeSeries(series); // Remove the series
          delete this.series[uniqueKey]; // Delete the series reference
          console.debug('Removed series:', uniqueKey);
        } else {
          console.warn('Series not found for:', uniqueKey);
        }
      }

      // Remove the tag from activeIndicators
      this.activeIndicators.splice(index, 1);

      console.debug('Active indicators after removal:', this.activeIndicators);
      console.log('Remaining series:', Object.keys(this.series));
    },

    addIndicator(indicatorType, params) {
      const uniqueKey = this.generateUniqueKey(indicatorType, params);

      if (this.series[uniqueKey]) {
        console.warn(`Indicator "${uniqueKey}" already exists.`);
        return;
      }

      // Generate a formatted title with only parameter values
      const formattedTitle = `${indicatorType.toUpperCase()}(${Object.values(params)
          .filter(value => typeof value !== 'string' || !value.startsWith('#')) // Exclude color values
          .join(', ')})`;

      let lineSeries;

      // Définir des échelles spécifiques pour RSI, MACD et ATR
      const priceScaleId =
          indicatorType === 'rsi'
              ? 'rsi'
              : indicatorType === 'macd'
                  ? 'macd'
                  : indicatorType === 'atr'
                      ? 'atr'
                      : undefined;

      if (priceScaleId) {
        // Ajouter une série avec une échelle spécifique
        lineSeries = this.chartInstance.addLineSeries({
          title: formattedTitle,
          color: params.color || this.generateRandomColor(),
          lineWidth: 2,
          priceScaleId: priceScaleId,
        });

        // Appliquer des marges spécifiques pour les échelles
        this.chartInstance.priceScale(priceScaleId).applyOptions({
          scaleMargins:
              priceScaleId === 'rsi'
                  ? { top: 0.7, bottom: 0.05 } // 30% pour RSI
                  : priceScaleId === 'macd'
                      ? { top: 0.5, bottom: 0.3 } // 20% pour MACD
                      : { top: 0.8, bottom: 0.1 }, // 10% pour ATR
          borderVisible: false,
        });
      } else {
        // Ajouter une série normale pour d'autres indicateurs
        lineSeries = this.chartInstance.addLineSeries({
          title: formattedTitle,
          color: params.color || this.generateRandomColor(),
          lineWidth: 2,
        });
      }

      lineSeries.customTitle = uniqueKey;

      const closingPrices = this.data.map(item => item.close);
      const highPrices = this.data.map(item => item.high);
      const lowPrices = this.data.map(item => item.low);
      let indicatorData = [];

      switch (indicatorType) {
        case 'sma':
          indicatorData = sma({ ...params, values: closingPrices }).map((value, index) => ({
            time: this.data[index + params.period - 1]?.time,
            value,
          }));
          break;

        case 'ema':
          indicatorData = ema({ ...params, values: closingPrices }).map((value, index) => ({
            time: this.data[index + params.period - 1]?.time,
            value,
          }));
          break;

        case 'wma':
          indicatorData = wma({ ...params, values: closingPrices }).map((value, index) => ({
            time: this.data[index + params.period - 1]?.time,
            value,
          }));
          break;

        case 'rsi':
          indicatorData = rsi({ ...params, values: closingPrices }).map((value, index) => ({
            time: this.data[index + params.period]?.time,
            value,
          }));
          break;

        case 'macd': {
          const macdResults = macd({ ...params, values: closingPrices });

          // Data for MACD, signal line, and histogram
          const macdLine = macdResults.map((result, index) => ({
            time: this.data[index + params.slowPeriod - 1]?.time,
            value: result.MACD,
          }));

          const signalLine = macdResults.map((result, index) => ({
            time: this.data[index + params.slowPeriod - 1]?.time,
            value: result.signal,
          }));

          const histogram = macdResults.map((result, index) => ({
            time: this.data[index + params.slowPeriod - 1]?.time,
            value: result.histogram,
            color: result.histogram >= 0 ? '#2ECC71' : '#E74C3C', // Green for positive, red for negative
          }));

          // Add MACD line
          const macdSeries = this.chartInstance.addLineSeries({
            title: `${formattedTitle} - MACD`,
            color: params.color || this.generateRandomColor(),
            lineWidth: 2,
            priceScaleId: 'macd',
          });

          macdSeries.setData(macdLine);
          macdSeries.customTitle = `${uniqueKey}-macd`; // Set custom title
          this.series[`${uniqueKey}-macd`] = macdSeries;

          // Add signal line
          const signalSeries = this.chartInstance.addLineSeries({
            title: `${formattedTitle} - Signal`,
            color: this.lightenColor(params.color || this.generateRandomColor(), 0.7), // Lighter color for signal
            lineWidth: 1,
            lineStyle: 1, // Dashed line
            priceScaleId: 'macd',
          });

          signalSeries.setData(signalLine);
          signalSeries.customTitle = `${uniqueKey}-signal`;
          this.series[`${uniqueKey}-signal`] = signalSeries;

          // Add histogram
          const histogramSeries = this.chartInstance.addHistogramSeries({
            title: `${formattedTitle} - Histogram`,
            priceScaleId: 'macd',
            base: 0, // Center the histogram around 0
          });

          histogramSeries.setData(histogram);
          histogramSeries.customTitle = `${uniqueKey}-histogram`;
          this.series[`${uniqueKey}-histogram`] = histogramSeries;

          // Configure MACD-specific price scale
          this.chartInstance.priceScale('macd').applyOptions({
            scaleMargins: { top: 0.8, bottom: 0.05 }, // Position MACD below the chart
            borderVisible: false,
          });

          console.debug('Added MACD with Signal and Histogram:', uniqueKey);
          return;
        }

        case 'atr':
          indicatorData = atr({
            ...params,
            high: highPrices,
            low: lowPrices,
            close: closingPrices,
          }).map((value, index) => ({
            time: this.data[index + params.period]?.time,
            value,
          }));
          break;

        case 'bollingerbands':
          const bbResults = bollingerbands({ ...params, values: closingPrices });

          const middleBand = bbResults.map((result, index) => ({
            time: this.data[index + params.period - 1]?.time,
            value: result.middle,
          }));

          const upperBand = bbResults.map((result, index) => ({
            time: this.data[index + params.period - 1]?.time,
            value: result.upper,
          }));

          const lowerBand = bbResults.map((result, index) => ({
            time: this.data[index + params.period - 1]?.time,
            value: result.lower,
          }));

          const middleSeries = this.chartInstance.addLineSeries({
            color: params.color || this.generateRandomColor(),
            lineWidth: 2,
          });
          middleSeries.setData(middleBand);
          middleSeries.customTitle = uniqueKey;

          const upperSeries = this.chartInstance.addLineSeries({
            color: this.lightenColor(params.color || this.generateRandomColor(), 0.5),
            lineWidth: 1,
            lineStyle: 1,
          });
          upperSeries.setData(upperBand);

          const lowerSeries = this.chartInstance.addLineSeries({
            color: this.lightenColor(params.color || this.generateRandomColor(), 0.5),
            lineWidth: 1,
            lineStyle: 1,
          });
          lowerSeries.setData(lowerBand);

          this.series[uniqueKey] = middleSeries;
          this.series[`${uniqueKey}-upper`] = upperSeries;
          this.series[`${uniqueKey}-lower`] = lowerSeries;

          console.debug('Added Bollinger Bands:', uniqueKey);
          return;

        default:
          console.error(`Unsupported indicator type: ${indicatorType}`);
          return;
      }

      if (indicatorData.length > 0) {
        lineSeries.setData(indicatorData);
        this.series[uniqueKey] = lineSeries;
      }

      console.debug('Added series:', uniqueKey, this.series[uniqueKey]);
    },

    clearIndicators() {
      // Supprimer toutes les séries liées aux indicateurs actifs
      this.activeIndicators.forEach((indicator) => {
        const uniqueKey = indicator.uniqueKey;

        if (uniqueKey.includes('macd')) {
          const keysToRemove = [`${uniqueKey}-macd`, `${uniqueKey}-signal`, `${uniqueKey}-histogram`];
          keysToRemove.forEach((key) => {
            if (this.series[key]) {
              this.chartInstance.removeSeries(this.series[key]);
              delete this.series[key];
            }
          });
        } else if (uniqueKey.includes('bollingerbands')) {
          const keysToRemove = [`${uniqueKey}`, `${uniqueKey}-upper`, `${uniqueKey}-lower`];
          keysToRemove.forEach((key) => {
            if (this.series[key]) {
              this.chartInstance.removeSeries(this.series[key]);
              delete this.series[key];
            }
          });
        } else {
          if (this.series[uniqueKey]) {
            this.chartInstance.removeSeries(this.series[uniqueKey]);
            delete this.series[uniqueKey];
          }
        }
      });

      // Réinitialiser les indicateurs actifs
      this.activeIndicators = [];
      console.debug('All indicators cleared.');
    },

    lightenColor(color, percent) {
      const num = parseInt(color.slice(1), 16),
          amt = Math.round(2.55 * percent),
          R = (num >> 16) + amt,
          G = ((num >> 8) & 0x00ff) + amt,
          B = (num & 0x0000ff) + amt;
      return (
          '#' +
          (
              0x1000000 +
              (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
              (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
              (B < 255 ? (B < 1 ? 0 : B) : 255)
          )
              .toString(16)
              .slice(1)
      );
    },

    generateRandomColor() {
      const getBrightValue = () => Math.floor(Math.random() * 128) + 128; // Valeurs entre 128 et 255
      const r = getBrightValue().toString(16).padStart(2, '0'); // Rouge
      const g = getBrightValue().toString(16).padStart(2, '0'); // Vert
      const b = getBrightValue().toString(16).padStart(2, '0'); // Bleu
      return `#${r}${g}${b}`;
    },

    generateUniqueKey(type, params = {}) {
      // Default params to an empty object if undefined
      const { color, ...restParams } = params;

      // Sort the properties for consistency
      const sortedParams = Object.keys(restParams).sort().reduce((obj, key) => {
        obj[key] = restParams[key];
        return obj;
      }, {});

      const uniqueKey = `${type}-${JSON.stringify(sortedParams)}`;
      return uniqueKey;
    },

    /**
     * Ajouter les indicateurs retournés par le serveur au graphique
     */
    addIndicatorsFromServer(indicators) {
      indicators.forEach((indicator) => {
        const { type, params } = indicator;

        // Vérifiez ou générez une couleur
        const color = params.color || this.generateRandomColor(); // Utiliser la couleur fournie ou en générer une

        // Exclure la couleur pour générer la clé unique
        const { color: _, ...filteredParams } = params;
        const uniqueKey = this.generateUniqueKey(type, filteredParams);

        // Vérifiez si l'indicateur est déjà actif
        if (this.activeIndicators.some((ind) => ind.uniqueKey === uniqueKey)) {
          console.warn(`Indicator "${uniqueKey}" already exists.`);
          return;
        }

        // Ajouter l'indicateur au graphique avec la bonne couleur
        const updatedParams = { ...params, color }; // Inclure la couleur
        this.addIndicator(type, updatedParams);

        // Ajouter l'indicateur à la liste des indicateurs actifs
        this.activeIndicators.push({
          type,
          label: `${type.toUpperCase()}(${Object.values(filteredParams).join(', ')})`,
          params: updatedParams, // Inclure la couleur dans les paramètres actifs
          uniqueKey,
          color,
          currentValue: null, // Initialiser la valeur actuelle
        });
      });

      console.debug('Indicators added from server:', indicators);
    },

    flattenParams(params, parentKey = '', result = {}) {
      for (const key in params) {
        const value = params[key];
        const newKey = parentKey ? `${parentKey}.${key}` : key; // Concaténer les clés avec un séparateur

        if (typeof value === 'object' && value !== null) {
          // Récursion pour les objets imbriqués
          this.flattenParams(value, newKey, result);
        } else {
          // Ajouter les valeurs simples directement
          result[newKey] = value;
        }
      }
      return result;
    },

    // Méthode pour restaurer les données aplaties vers leur structure originale
    unflattenParams(flatParams) {
      const result = {};
      for (const flatKey in flatParams) {
        const keys = flatKey.split('.');
        keys.reduce((acc, key, index) => {
          if (index === keys.length - 1) {
            acc[key] = flatParams[flatKey];
          } else {
            acc[key] = acc[key] || {};
          }
          return acc[key];
        }, result);
      }
      return result;
    },

    formatParams(params) {
      return Object.values(params).filter(val => typeof val !== 'string' || !val.startsWith('#')).join(', ');
    },

    getIndicatorValue(indicator) {
      const foundIndicator = this.displayedIndicators.find(
          (ind) => ind.type === indicator.type && JSON.stringify(ind.params) === JSON.stringify(indicator.params)
      );
      return foundIndicator ? foundIndicator.displayValue : 'N/A';
    },

    calculateIndicatorValue(indicator, candle) {
      // Trouver l'index de la bougie actuelle
      const candleIndex = this.data.findIndex(item => item.time === candle.time);
      if (candleIndex === -1) return 'N/A';

      // Extraire les données jusqu'à la bougie actuelle
      const closePrices = this.data.slice(0, candleIndex + 1).map(item => item.close);
      const highPrices = this.data.slice(0, candleIndex + 1).map(item => item.high);
      const lowPrices = this.data.slice(0, candleIndex + 1).map(item => item.low);

      switch (indicator.type) {
        case 'sma':
          return sma({ period: indicator.params.period, values: closePrices }).pop()?.toFixed(2) || 'N/A';

        case 'ema':
          return ema({ period: indicator.params.period, values: closePrices }).pop()?.toFixed(2) || 'N/A';

        case 'wma':
          return wma({ period: indicator.params.period, values: closePrices }).pop()?.toFixed(2) || 'N/A';

        case 'rsi':
          return rsi({ period: indicator.params.period, values: closePrices }).pop()?.toFixed(2) || 'N/A';

        case 'macd': {
          if (closePrices.length < indicator.params.slowPeriod + indicator.params.signalPeriod) return "N/A";

          const result = macd({
            fastPeriod: indicator.params.fastPeriod,
            slowPeriod: indicator.params.slowPeriod,
            signalPeriod: indicator.params.signalPeriod,
            values: closePrices,
            SimpleMAOscillator: false,
            SimpleMASignal: false,
          }).pop();

          return result
              ? `fa ${result.MACD.toFixed(2)} | so ${result.signal.toFixed(2)} | si ${result.histogram.toFixed(2)}`
              : "N/A";
        }

        case 'atr':
          return atr({ period: indicator.params.period, high: highPrices, low: lowPrices, close: closePrices }).pop()?.toFixed(2) || 'N/A';

        case 'bollingerbands': {
          const result = bollingerbands({
            period: indicator.params.period,
            stdDev: indicator.params.stdDev,
            values: closePrices,
          }).pop();

          return result
              ? `up ${result.upper.toFixed(2)} | mid ${result.middle.toFixed(2)} | lo ${result.lower.toFixed(2)}`
              : "N/A";
        }

        default:
          return 'N/A';
      }
    },
    updateIndicators(candle) {
      this.displayedIndicators = this.displayedIndicators.map(indicator => {
        return {
          ...indicator,
          displayValue: this.calculateIndicatorValue(indicator, candle)
        };
      });
    },
    async fetchSymbols() {
      try {
        const response = await evalApi.get('/binanceSymbols');
        this.symbolCounts = response.data;
        this.sortedSymbols = Object.keys(response.data).sort((a, b) => response.data[b] - response.data[a]);
      } catch (error) {
        console.error("❌ Error fetching symbols:", error);
      }
    },
    async evaluate() {
      this.loading = true; // ✅ Active le loader
      try {
        // ✅ Ajouter l'offset avant conversion en UTC
        function convertToUTC(dateString) {
          const date = new Date(dateString);
          return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString();
        }

        const startDateUTC = convertToUTC(this.startDate);
        const endDateUTC = convertToUTC(this.endDate);

        console.log(`🕒 Conversion UTC+1 ➝ UTC : ${this.startDate} → ${startDateUTC}`);
        console.log(`🕒 Conversion UTC+1 ➝ UTC : ${this.endDate} → ${endDateUTC}`);

        const response = await evalApi.post('/evaluate', {
          symbol: this.selectedSymbol.includes('USDT') ? this.selectedSymbol : `${this.selectedSymbol}USDT`,
          startDate: startDateUTC,
          endDate: endDateUTC,
          interval: this.selectedInterval,
          saveToJson: this.saveToJson,
        });

        console.log("response.data.candles:",response.data.candles);

        // 🔥 Conversion des timestamps pour le format UTC+1
        this.data = response.data.candles.map(item => ({
          ...item,
          time: item.time- this.timeDifference
        }));

        // 🔄 Mise à jour du graphique
        this.updateChart();

      } catch (error) {
        console.error("❌ Erreur API:", error);
      } finally {
        this.loading = false; // ✅ Désactive le loader à la fin
      }
    },

    updateChart() {
      const chartContainer = this.$refs.chartContainer;
      if (!chartContainer) return;

      // Update chart dimensions
      const resizeObserver = new ResizeObserver(() => {
        this.chartInstance.applyOptions({
          width: chartContainer.offsetWidth,
          height: chartContainer.offsetHeight,
        });
      });

      resizeObserver.observe(chartContainer);

      // Set the candlestick data
      this.candlestickSeries.setData(this.data);

      // Apply reset zoom to ensure correct scaling
      this.resetZoom();
    },
  },

  watch: {
    selectedIndicator() {
      this.updateIndicatorParams();
    },
  },

  computed: {
    indicatorsWithValues() {
      return this.activeIndicators.map((indicator) => {
        if (indicator.type === 'macd') {
          // Concaténer les valeurs du MACD
          const macdValue = this.macdValues.macd;
          const signalValue = this.macdValues.signal;
          const histogramValue = this.macdValues.histogram;

          return {
            ...indicator,
            displayValue: `m ${macdValue} | s ${signalValue} | h ${histogramValue}`,
          };
        }

        // Pour les autres indicateurs
        return {
          ...indicator,
          displayValue: indicator.currentValue || 'N/A',
        };
      });
    },

    displayedIndicatorsWithValues() {
      return this.displayedIndicators.map((indicator) => {
        return {
          ...indicator,
          displayValue: this.getIndicatorValue(indicator) || 'N/A',
        };
      });
    },

    allIndicators() {
      return [...this.displayedIndicators, ...this.japaneseCandlesticks.map(name => ({ label: name, displayValue: "N/A" }))];
    },
    splitIndicators() {
      const columns = 6;
      const rows = [];
      const indicators = [...this.allIndicators];
      while (indicators.length) {
        rows.push(indicators.splice(0, columns));
      }
      return rows;
    },
    formattedDateRange() {
      if (!this.startDate || !this.endDate) return "No date selected";
      const start = new Date(this.startDate).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const end = new Date(this.endDate).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      return `${start} → ${end}`;
    }
  },

  mounted() {
    this.initializeChart();
    this.fetchAvailableFiles();
    window.addEventListener('resize', this.updateChart); // Ensure the chart resizes on window resize
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateChart); // Cleanup the event listener
  },
};
</script>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background-color: #121212;
  color: #fff;
  min-height: 100vh;
}

/* Widget pour le graphique et la recherche */
.widget {
  background-color: #1e2126;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.chart-widget {
  display: flex;
  /*flex-grow: 2;*/
}

.getButton {
  background-color: #26a69a;
  color: white;
  border: none;
  padding: 3px 3px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  /*width: 40%;*/
  margin-top: 10px;
  align-self: flex-start;
  height: 30px;
}

button:hover {
  background-color: #1b8c76;
}

table {
  width: 100%;
}

table th,
table td {
  padding: 10px 20px;
}

table th {
  position: sticky;
  top: 0;
  background-color: #333;
  color: #fff;
  z-index: 1;
}

input[type="number"], input[type="text"], select, input[type="date"] {
  width: 72%;
  padding: 5px;
  font-size: 14px;
  background-color: #333;
  border: 1px solid #555;
  border-radius: 5px;
  color: #fff;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0; /* Remove padding */
  margin-bottom: 0; /* Reduce space below header */
  font-size: 14px;
  color: #fff;
}

.chart-container {
  height: 500px;
  width: 100%;
  margin-top: 0;
}

.pair-info {
  font-weight: bold;
}

.header-buttons {
  display: flex; /* Set flexbox layout */
  align-items: center; /* Align items vertically */
  gap: 10px; /* Add space between buttons */
}

.header-buttons-with-tags {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px; /* Adjust spacing between tags and buttons */
}

.add-indicator-btn {
  background-color: #26a69a;
  color: white;
  border: none;
  padding: 5px 10px; /* Adjust padding for better appearance */
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  width: auto; /* Let the button size adjust to its content */
  height: 30px;
  margin-top: 0; /* Remove margin to align with the reset button */
}

.reset-icon {
  font-size: 24px;
  cursor: pointer;
  display: flex; /* Align the refresh icon */
  justify-content: center;
  align-items: center;
  height: 30px; /* Match the height of the button */
  width: 30px; /* Set width for consistency */
}

.reset-icon:hover {
  color: #26a69a;
}

.add-indicator-btn:hover {
  background-color: #2980b9;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.indicator-tags {
  display: flex;
  align-items: center;
  gap: 10px; /* Espace entre les tags */
  flex-wrap: nowrap; /* Empêche le retour à la ligne */
  overflow-x: auto; /* Ajoute un défilement horizontal si nécessaire */
}

.indicator-color {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.remove-icon {
  cursor: pointer;
  font-size: 14px;
  color: white;
  background: none;
  padding: 0;
}

.remove-icon:hover {
  color: #ccc;
}

/* popup indicateur*/
.indicator-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background-color: #1e2126;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  width: 400px;
  max-width: 90%;
  color: #fff;
}

.popup-header {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
  text-align: center;
}

.popup-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.popup-label {
  font-size: 14px;
  margin-bottom: 5px;
}

.popup-select,
.popup-input {
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #555;
  border-radius: 5px;
  background-color: #333;
  color: #fff;
  outline: none;
}

.popup-select:focus,
.popup-input:focus {
  border-color: #26a69a;
}

.popup-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.popup-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.popup-button {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
}

.popup-cancel {
  background-color: #e74c3c;
  color: #fff;
}

.popup-cancel:hover {
  background-color: #c0392b;
}

.popup-add {
  background-color: #26a69a;
  color: #fff;
}

.popup-add:hover {
  background-color: #1b8c76;
}

.popup-footer button {
  background-color: #26a69a; /* Identique au bouton Add */
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.popup-footer button:hover {
  background-color: #1b8c76; /* Légère différence pour hover */
}

/* Formulaire */
.container label,
.container select,
.container input,
.container button {
  font-size: 12px; /* Réduire la taille de la police */
}

/* Tableau */
table th,
table td {
  font-size: 12px; /* Réduire la taille de la police */
  padding: 8px; /* Réduire l'espace entre les cellules */
}

table th {
  font-weight: bold;
}

.getButton {
  font-size: 12px;
  padding: 5px 10px;
}

.reset-icon {
  font-size: 24px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  width: 30px;
}

.reset-icon:hover {
  color: #26a69a;
}

.trades-widget table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.trades-widget th,
.trades-widget td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #333;
}

.trades-widget th {
  background-color: #333;
  color: #fff;
}

/* 📌 Conteneur des widgets */
.widget-container {
  display: flex;
  justify-content: space-between; /* ✅ Espacement uniforme */
  gap: 15px; /* ✅ Évite un chevauchement */
  margin-top: 10px;
}

/* 📊 Widgets des exchanges */
.exchange-widget {
  flex: 1; /* ✅ Chaque widget prend 1/3 de l'espace */
  background-color: #1e2126;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  padding: 15px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 120px; /* ✅ Assure une hauteur uniforme */
}

/* ✅ Contenu des widgets */
.exchange-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.widget h3 {
  margin-bottom: 10px; /* Uniformiser l'espacement sous le titre */
  font-size: 18px; /* Taille du titre pour cohérence */
}

.trades-widget table {
  margin-top: 0; /* Supprime l'espacement supplémentaire au-dessus du tableau */
}

.widget table {
  margin-top: 0; /* Applique aussi cette règle aux autres tableaux dans les widgets */
}

.popup-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.popup-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.popup-label {
  font-size: 14px;
  margin-bottom: 5px;
}

.popup-input,
.popup-select {
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #555;
  border-radius: 5px;
  background-color: #333;
  color: #fff;
  outline: none;
}

.popup-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.popup-button {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
}

.popup-cancel {
  background-color: #e74c3c;
  color: #fff;
}

.popup-cancel:hover {
  background-color: #c0392b;
}

.popup-add {
  background-color: #26a69a;
  color: #fff;
}

.popup-add:hover {
  background-color: #1b8c76;
}

.ohlc-info {
  font-weight: 300; /* Police plus légère */
  margin-left: 10px; /* Proche de .pair-info */
  text-align: left;
  display: inline-block;
}

.indicator-widget {
  background-color: #1e2126;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  color: white;
}

.indicator-widget table {
  width: 100%;
  border-collapse: collapse;
}

.indicator-box {
  width: 25%; /* Chaque colonne fait 25% de largeur */
  padding: 10px;
  text-align: center;
  border: none;
}

.toggle-indicator-widget {
  margin-left: 10px;
  cursor: pointer;
}

.compact-input {
  font-size: 13px;
  height: 28px; /* ✅ Taille minimale */
  padding: 3px 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.select-input {
  width: auto; /* ✅ S’adapte au contenu */
  min-width: 120px; /* ✅ Largeur minimale pour éviter un écrasement */
}

.date-input {
  width: auto;
  min-width: 160px; /* ✅ Ajustement pour la lisibilité des dates */
}

.getButton {
  background-color: #26a69a;
  color: white;
  border: none;
  padding: 5px 10px; /* Adjust padding for better appearance */
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  width: auto; /* Let the button size adjust to its content */
  height: 30px;
  margin-top: 0; /* Remove margin to align with the reset button */
}

.getButton:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.exchange-widget {
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* ✅ Aligner tout en haut */
  justify-content: flex-start; /* ✅ Empêche l'effet de centrage */
  height: 100%; /* ✅ Prend toute la hauteur du widget */
}

.traders-table {
  width: 100%;
  align-self: flex-start; /* ✅ Garde le tableau collé au top */
}

.traders-table th, .traders-table td {
  padding: 5px;
  text-align: left;
  border-bottom: 1px solid #444;
}

.traders-table th {
  background-color: #333;
  color: white;
}

.traders-table a {
  color: #3498db;
  text-decoration: none;
}

.traders-table a:hover {
  text-decoration: underline;
}

.exchange-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.exchange-header label {
  display: flex;
  align-items: center;
  font-size: 14px;
  gap: 5px;
}

.chart-header-container {
  display: flex;
  align-items: center;
  justify-content: space-between; /* Sépare bien scores à gauche et chart-controls à droite */
  padding: 10px 0;
}

.live-scores {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 14px;
}

.score-value {
  display: inline-block;
  width: 50px; /* 🔥 Ajuste cette valeur en fonction de la plus grande valeur possible */
  text-align: right; /* Aligner les chiffres à droite pour un affichage uniforme */
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.loader {
  width: 20px;
  height: 20px;
  border: 3px solid #ddd;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

input[type="file"] {
  display: block;
}

.chart-header-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.position-controls {
  padding: 0.5rem 1rem;
  background-color: #1e1e1e;
  border-color: #333;
  border: 1px solid #ccc;
  border-radius: 6px;
  margin-top: 1rem;
  max-width: 400px;
}

.position-file-list {
  list-style: none;
  padding-left: 0;
  margin-top: 0.5rem;
}

.file-entry {
  padding: 0.4rem 0.75rem;
  margin-bottom: 0.25rem;
  background-color: #2a2a2a;
  border: 1px solid #444;
  color: #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.file-entry:hover {
  background-color: #333;
  color: #00bfff;
}

</style>
